"use client";

import { c as _c } from "react/compiler-runtime";
import type { CategoryEnum } from "@sprawdzonabron/shared";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { Suspense } from "react";
import type { getAvailableFilters } from "../../api/services/getAvailableFilters";
import { getProducts } from "../../api/services/getProducts";
import type { getShops } from "../../api/services/getShops";
import { FiltersSync } from "../../features/filters/FiltersSync";
import { useWindowSize } from "../../hooks/useWindowSize";
import { selectMobileMapVisible, useSelector } from "../../store/selectors";
import { Filters as FiltersComponent } from "../Filters/Filters";
import { Products } from "../Products/Products";
import { useProducts } from "./useProducts";
const Map = dynamic(() => import("../Map/Map").then(mod => mod.Map),
// Obsługa named export
{
  ssr: false,
  loading: () => <aside aria-label="Mapa dostępności" className="hidden md:block flex-1 min-w-0 h-[calc(100vh_-58px)] sticky top-[52px]">
        <div className="w-full h-full bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse flex items-center justify-center">
          <span className="text-slate-400 text-sm">Ładowanie mapy...</span>
        </div>
      </aside>
});
interface PageContentProps {
  availableFilters: Awaited<ReturnType<typeof getAvailableFilters>>;
  initialProducts: Awaited<ReturnType<typeof getProducts>>;
  shops: Awaited<ReturnType<typeof getShops>>;
  defaultCategory?: CategoryEnum;
  defaultCity?: string;
  seoText?: string;
  defaultSearch?: string;
  defaultProducer?: string[];
  isHomePage?: boolean;
}
export const PageContent = t0 => {
  const $ = _c(36);
  const {
    availableFilters,
    initialProducts,
    shops,
    defaultCity,
    defaultCategory,
    seoText,
    defaultSearch,
    defaultProducer,
    isHomePage: t1
  } = t0;
  const isHomePage = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== defaultCategory || $[1] !== defaultCity || $[2] !== defaultProducer || $[3] !== defaultSearch || $[4] !== initialProducts) {
    t2 = {
      initialProducts,
      defaultCategory,
      defaultSearch,
      defaultProducer,
      defaultCity
    };
    $[0] = defaultCategory;
    $[1] = defaultCity;
    $[2] = defaultProducer;
    $[3] = defaultSearch;
    $[4] = initialProducts;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    hasMore,
    isLoading,
    error,
    loadMore
  } = useProducts(t2);
  const {
    isMobile
  } = useWindowSize();
  const mobileMapVisible = useSelector(selectMobileMapVisible);
  const shouldRenderMap = !isMobile || mobileMapVisible;
  let t3;
  if ($[6] !== defaultCategory || $[7] !== defaultCity || $[8] !== defaultProducer || $[9] !== defaultSearch) {
    t3 = <Suspense fallback={null}><FiltersSync defaultSearch={defaultSearch} defaultProducer={defaultProducer} defaultCity={defaultCity} defaultCategory={defaultCategory} /></Suspense>;
    $[6] = defaultCategory;
    $[7] = defaultCity;
    $[8] = defaultProducer;
    $[9] = defaultSearch;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== availableFilters) {
    t4 = <div className="flex-shrink-0"><FiltersComponent availableFilters={availableFilters} /></div>;
    $[11] = availableFilters;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== data || $[14] !== error || $[15] !== hasMore || $[16] !== isLoading || $[17] !== loadMore || $[18] !== seoText) {
    t5 = <Products data={data} hasMore={hasMore} isLoading={isLoading} error={error} loadMore={loadMore} seoText={seoText} />;
    $[13] = data;
    $[14] = error;
    $[15] = hasMore;
    $[16] = isLoading;
    $[17] = loadMore;
    $[18] = seoText;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== isHomePage) {
    t6 = !isHomePage && <div className="mt-6"><p className="text-sm  text-center text-slate-750 dark:text-slate-300">Nie znalazłeś interesujących Cię ofert? Wróć na stronę główną i skorzystaj z wyszukiwarki{" "}<Link href="/" className="text-brand-600 hover:text-brand-500 hover:underline font-medium" title={"Wyszukiwarka i por\xF3wnywarka broni w ca\u0142ej Polsce - SprawdzonaBron.pl"}>SprawdzonaBron.pl</Link></p></div>;
    $[20] = isHomePage;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <div className="w-full md:w-[400px] xl:w-[600px] flex-shrink-0">{t5}{t6}</div>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== data || $[26] !== shops || $[27] !== shouldRenderMap) {
    t8 = shouldRenderMap && <Map shops={shops.data || {}} productsByShop={data.stats.productsByShop || {}} />;
    $[25] = data;
    $[26] = shops;
    $[27] = shouldRenderMap;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t4 || $[30] !== t7 || $[31] !== t8) {
    t9 = <div className="flex flex-col md:flex-row gap-2">{t4}{t7}{t8}</div>;
    $[29] = t4;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t3 || $[34] !== t9) {
    t10 = <div className="flex justify-center"><div className="container mx-auto">{t3}{t9}</div></div>;
    $[33] = t3;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  return t10;
};